import React, { useCallback, useMemo, useState } from "react";
import { Box, Chip, IconButton } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import PlayableFretboardDiagram, { FretboardMarker } from "../components/PlayableFretboardDiagram";
import FretboardSettingsSheet from "../components/FretboardSettingsSheet";
import { useGlobalSettings } from "../GlobalSettingsContext";
import { HIGHEST_FRET, describeFretWindow } from "../fretWindow";
import { DEGREE_COLORS } from "../library/colors";
import { NOTE_NAMES, formatNoteName, getGuitarPitchClass, pitchClassOf } from "../library/notes";
import {
  OVERLAY_DEGREE_LABELS,
  findOverlayPositions,
  overlayNotes,
  overlayTitle,
} from "../library/overlays";

const Explore: React.FC = () => {
  const {
    settings: { overlayKind, overlayRootPitchClass, fretWindow, letRing, volume },
  } = useGlobalSettings();
  const [sheetOpen, setSheetOpen] = useState(false);
  const openSheet = () => setSheetOpen(true);

  const markers: FretboardMarker[] = useMemo(() => {
    if (overlayKind === "none") return [];
    const labels = OVERLAY_DEGREE_LABELS[overlayKind];
    return findOverlayPositions(
      overlayRootPitchClass,
      overlayKind,
      fretWindow.startFret,
      fretWindow.endFret
    ).map((p) => ({
      stringNum: p.stringNum,
      fretNum: p.fretNum,
      label: labels[p.degree],
      color: DEGREE_COLORS[p.degree],
    }));
  }, [overlayKind, overlayRootPitchClass, fretWindow]);

  // A tapped note is named in the overlay's spelling when it belongs to it, and by its dual
  // name otherwise — there is no key to spell it against.
  const tapLabel = useCallback(
    (stringNum: number, fretNum: number) => {
      const pitchClass = getGuitarPitchClass(stringNum, fretNum);
      const inOverlay = overlayNotes(overlayRootPitchClass, overlayKind).find(
        (note) => pitchClassOf(note.noteName) === pitchClass
      );
      return formatNoteName(inOverlay?.noteName ?? NOTE_NAMES[pitchClass]);
    },
    [overlayKind, overlayRootPitchClass]
  );

  return (
    <>
      {/* Summary row: what the board is showing, each chip opening the sheet that changes it.
          Capped to the fretboard SVG width (~350px) so it lines up with the diagram below. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: 350, mb: 1.5 }}>
        <Chip
          variant="outlined"
          onClick={openSheet}
          icon={
            overlayKind === "none" ? undefined : (
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: DEGREE_COLORS[1],
                  ml: "8px !important",
                }}
              />
            )
          }
          label={overlayTitle(overlayRootPitchClass, overlayKind)}
          sx={{ fontWeight: 600 }}
        />
        <Chip variant="outlined" onClick={openSheet} label={describeFretWindow(fretWindow)} />
        <IconButton aria-label="fretboard settings" onClick={openSheet} sx={{ ml: "auto" }}>
          <TuneIcon />
        </IconButton>
      </Box>

      <PlayableFretboardDiagram
        markers={markers}
        startFret={fretWindow.startFret}
        numFretsToShow={fretWindow.endFret - fretWindow.startFret + 1}
        maxFret={HIGHEST_FRET}
        tapLabel={tapLabel}
        letRing={letRing}
        volume={volume}
      />

      <FretboardSettingsSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
};

export default Explore;
